import { Edit } from "@mui/icons-material";
import { Box, Button, CircularProgress, Tab, Tabs } from "@mui/material";
import { DataGridPremium, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import {
	useGetAllContractorRollingNumbersLazyQuery,
	useGetAllContractorsForRollingNumbersQuery,
	useGetAllGlobalRollingNumbersLazyQuery,
} from "@repo/server-api";
import { RollingNumber } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useState } from "react";

import DialogRollingNumber from "@repo/legacy/components/shared/dialogs/DialogRollingNumber";

type ContractorTab = {
	id: string;
	displayName: string;
};

export default function RollingNumbers() {
	const { t } = useTranslation();

	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [rollingNumber, setRollingNumber] = useState<RollingNumber | null>(null);
	const [rollingNumbers, setRollingNumbers] = useState<RollingNumber[]>([]);
	const [selectedTab, setSelectedTab] = useState<string>("global");
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const { data: contractorsData } = useGetAllContractorsForRollingNumbersQuery({
		fetchPolicy: "cache-and-network",
	});

	const contractors: ContractorTab[] = useMemo(() => {
		if (contractorsData?.allContacts?.nodes) {
			return [...contractorsData.allContacts.nodes].sort((a, b) =>
				a.displayName.localeCompare(b.displayName)
			);
		}
		return [];
	}, [contractorsData]);

	const [getAllRollingNumbers] = useGetAllGlobalRollingNumbersLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.allGlobalRollingNumbers) {
				setRollingNumbers(data.allGlobalRollingNumbers);
			}
			setIsLoading(false);
		},
	});

	const [getContractorRollingNumbers] = useGetAllContractorRollingNumbersLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.allContractorRollingNumbers) {
				setRollingNumbers(data.allContractorRollingNumbers);
			}
			setIsLoading(false);
		},
	});

	useEffect(() => {
		getAllRollingNumbers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
		setSelectedTab(newValue);
		setRollingNumbers([]);
		setIsLoading(true);
		if (newValue === "global") {
			getAllRollingNumbers();
		} else {
			getContractorRollingNumbers({ variables: { contractorId: newValue } });
		}
	};

	const refreshData = () => {
		if (selectedTab === "global") {
			getAllRollingNumbers();
		} else {
			getContractorRollingNumbers({ variables: { contractorId: selectedTab } });
		}
	};

	const columns: GridColDef<RollingNumber>[] = useMemo(
		() => [
			{
				field: "type",
				headerName: t("type"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					const type = params.row.type.toLocaleLowerCase();
					return t(`rollingnumber.${type}`, { ns: "enum" });
				},
			},
			{
				field: "format",
				headerName: t("format"),
				width: 150,
			},
			{
				field: "currentValue",
				headerName: t("current-value"),
				width: 150,
			},
			{
				field: "digitCount",
				headerName: t("digit-count"),
				width: 150,
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 150,
				renderCell: (params: GridCellParams) => {
					const openEdit = (rn: RollingNumber | null) => {
						setRollingNumber(rn);
						setOpenDialog(true);
					};

					return (
						<div>
							<Button data-testid="edit-button" onClick={() => openEdit(params.row)}>
								<Edit />
							</Button>
						</div>
					);
				},
			},
		],
		[t],
	);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<h3 className="purpleHeader">{t("tab.rolling-numbers")}</h3>

			<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
				<Tabs
					value={selectedTab}
					onChange={handleTabChange}
					variant="scrollable"
					scrollButtons="auto"
					aria-label="rolling numbers tabs"
				>
					<Tab key="global" label={t("global")} value="global" data-testid="tab-global" />
					{contractors.map((contractor) => (
						<Tab
							key={contractor.id}
							label={contractor.displayName}
							value={contractor.id}
							data-testid={`tab-contractor-${contractor.id}`}
						/>
					))}
				</Tabs>
			</Box>

			{isLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
					<CircularProgress />
				</Box>
			) : (
				<DataGridPremium
					getRowId={(row: RollingNumber) => (row ? row.type : "")}
					rows={rollingNumbers}
					columns={columns}
					disableRowSelectionOnClick
					className="table"
					hideFooter={true}
				/>
			)}
			<DialogRollingNumber
				refreshData={refreshData}
				rollingNumber={rollingNumber}
				open={openDialog}
				setOpen={setOpenDialog}
			/>
		</Box>
	);
}
